// Home panel (amoeba-unification.md) — the mother's room, not a store.
// One purchase exists in the whole game: Anagenesis, the next evolution
// tier. Below it, the family album: adaptations held this run, and the
// ones that future tiers will add to the card pool (selling the ladder).

import type { Entity } from '../game/components'
import { triggerNarrator } from '../engine/narrator'
import type { NarratorKey } from '../engine/narrator-lines'
import { applyTier, canEvolve, currentTier, MAX_TIER, TIER_SPECS, type Tier } from '../game/evolution'
import { applyPlayerRadius } from '../game/growth'
import { POWER_DEFS, poolAtTier } from '../game/powers'

// Tiers whose evolve fires a generic `evolution_tierN` narrator line here at
// purchase time. Tier 3 is excluded: it speciates, and the per-form reveal
// line fires later from the burst (systems/narrator.ts on lineage.speciated),
// once the form is actually known — firing a generic line here too would
// double up and pre-empt the reveal.
const TIERS_WITH_NARRATOR_LINE = new Set<Tier>([2])

export interface HomePanel {
  isOpen(): boolean
  open(): void
  close(): void
  refresh(player: Entity): void
}

export function createHomePanel(onClose?: () => void, onEvolved?: () => void): HomePanel {
  const overlay = document.getElementById('skill-shop') as HTMLDivElement
  const dnaCount = document.getElementById('skill-shop-dna') as HTMLSpanElement
  const list = document.getElementById('skill-shop-list') as HTMLUListElement
  const closeBtn = document.getElementById('skill-shop-close') as HTMLButtonElement

  let open = false
  let currentPlayer: Entity | null = null

  function render() {
    if (!currentPlayer) return
    const player = currentPlayer
    const playerDna = player.dna ?? 0
    dnaCount.textContent = String(playerDna)
    list.innerHTML = ''

    // ── Anagenesis — the one purchase in the game ─────────────────────────
    const tier = currentTier(player)
    const result = canEvolve(player, playerDna)

    const li = document.createElement('li')
    const atMax = tier >= MAX_TIER
    const cls = atMax ? ' owned' : result.ok ? ' affordable' : ' locked'
    li.className = 'skill-item evolution' + cls

    const header = document.createElement('div')
    header.className = 'skill-header'
    const nameEl = document.createElement('span')
    nameEl.className = 'skill-name'
    nameEl.textContent = `Anagenesis — tier ${tier}`
    const costEl = document.createElement('span')
    costEl.className = 'skill-cost'
    costEl.textContent = atMax
      ? 'the lineage is complete'
      : `tier ${tier + 1} · ${TIER_SPECS[(tier + 1) as Tier].cost} DNA`
    header.appendChild(nameEl)
    header.appendChild(costEl)

    const descEl = document.createElement('div')
    descEl.className = 'skill-desc'
    descEl.textContent = atMax
      ? 'The advancement holds. Your mother is impressed, quietly.'
      : 'The lineage advances. Deeper water tolerates you; new adaptations enter the pool.'

    li.appendChild(header)
    li.appendChild(descEl)

    if (result.ok) {
      // Focus-nav target (Phase E): only affordable — the album rows below
      // have no click handler, so they're never keyboard-focusable either.
      // data-focus-initial: opening the shop lands here, not the close button.
      li.setAttribute('data-focus', '')
      li.setAttribute('data-focus-initial', '')
      li.tabIndex = -1
      li.addEventListener('click', () => {
        const re = canEvolve(player, player.dna ?? 0)
        if (!re.ok) return
        player.dna = (player.dna ?? 0) - re.cost
        applyTier(player, re.next, { animate: true })
        applyPlayerRadius(player)
        // Per-tier milestone line. Only a subset of tiers have authored lines
        // (tier 2, tier 3) — guard so a missing key is silently skipped.
        if (TIERS_WITH_NARRATOR_LINE.has(re.next)) {
          triggerNarrator(`evolution_tier${re.next}` as NarratorKey)
        }
        onEvolved?.()
        // Auto-close so the morph is the dominant visual — a tier-up should
        // not happen behind a menu.
        panel.close()
      })
    }
    list.appendChild(li)

    // ── The evolution ladder — tiers 1..MAX, current highlighted, each rung
    // labelled with the adaptations it opens into the card pool. Sells the
    // climb without re-listing every power; the Anagenesis row above is the
    // one actionable step. (monorail-edu, replacing the family album.)
    for (let t = 1 as Tier; t <= MAX_TIER; t = (t + 1) as Tier) {
      const isPast = t < tier
      const isCurrent = t === tier
      const isNext = t === tier + 1

      const row = document.createElement('li')
      row.className = 'skill-item tier'
        + (isPast ? ' owned' : '')
        + (isCurrent ? ' current' : '')
        + (isNext ? ' next' : '')
        + (!isPast && !isCurrent && !isNext ? ' locked' : '')

      const h = document.createElement('div')
      h.className = 'skill-header'
      const n = document.createElement('span')
      n.className = 'skill-name'
      n.textContent = isCurrent ? `Tier ${t} — you are here` : `Tier ${t}`
      const c = document.createElement('span')
      c.className = 'skill-cost'
      c.textContent = isPast
        ? 'reached'
        : isCurrent
          ? (t >= MAX_TIER ? 'lineage complete' : 'current')
          : `${TIER_SPECS[t].cost} DNA`
      h.appendChild(n)
      h.appendChild(c)

      // What this rung grants: powers that first enter the pool at tier t.
      const gained = poolAtTier(t).filter(id => !poolAtTier(t - 1).includes(id))
      const d = document.createElement('div')
      d.className = 'skill-desc'
      d.textContent = gained.length > 0
        ? `Adaptations: ${gained.map(id => POWER_DEFS[id].name).join(', ')}`
        : 'Deeper water tolerates you; the body hardens.'

      row.appendChild(h)
      row.appendChild(d)
      list.appendChild(row)
    }
  }

  closeBtn.addEventListener('click', () => panel.close())

  const panel: HomePanel = {
    isOpen: () => open,
    open() {
      open = true
      overlay.classList.add('visible')
      triggerNarrator('shop_open')
    },
    close() {
      open = false
      overlay.classList.remove('visible')
      onClose?.()
    },
    refresh(player: Entity) {
      currentPlayer = player
      render()
    },
  }

  return panel
}
